import { lazy, Suspense, useMemo } from 'react';

import { ChakraGrid } from '@equip.health/ui';
import { Redirect, Route, Switch } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { getStartingRouteByProviderTypeName } from '~/lib/util/nav.util';
import Loading from '~/pages/loading';

import PatientRoutes from './PatientRoutes';
import { Path } from './constants';
import { EAGER_MAUD_ROUTES, LAZY_MAUD_ROUTES, parseRoutePath } from './maudRoutes';
import MaudProviders from './providers/MaudProviders';
import AttachmentModal from '../components/common/AttachmentModal';
import MaudNav from '../components/common/navbar/MaudNav';
import Page from '../components/common/Page';
import { ComponentPortalContextProvider } from '../lib/contexts/ComponentPortalContext';
import { useGlobalContext } from '../lib/contexts/GlobalContext';
import GlobalAppSyncSubscriptionManager from '~/components/common/appsync/GlobalAppSyncSubscriptionManager';
import SafetyRiskAlertSubscriptionManager from '~/components/common/appsync/SafetyRiskAlertSubscriptionManager';
import SafetyRiskAlertContainer from '~/components/maud/safety-alert/SafetyRiskAlertContainer';

const LazyPageNotFoundPage = lazy(() => import('~/pages/404'));

const MaudApp = () => {
  const { currentUser } = useGlobalContext();
  const {
    aiSummaryAppSyncPoc: isAiSummaryAppSyncEnabled,
    phq9Enabled,
    routingRefactorPatientRoutes: isRoutingRefactorPatientRoutesEnabled,
  } = useFlags<LDFlags>();

  const parseEagerRoutes = (rawRoutes: typeof EAGER_MAUD_ROUTES) =>
    Object.keys(rawRoutes).map((route: string) => {
      const path = parseRoutePath(route);

      return { component: rawRoutes[route].default, path };
    });
  const parseLazyRoutes = (rawRoutes: typeof LAZY_MAUD_ROUTES) =>
    Object.keys(rawRoutes).map((route: string) => {
      const path = parseRoutePath(route);

      return { component: rawRoutes[route], path };
    });

  const eagerMaudRoutes = useMemo(() => {
    const routes = parseEagerRoutes(EAGER_MAUD_ROUTES);
    if (isRoutingRefactorPatientRoutesEnabled) {
      return routes.filter(
        (route) => route.path !== `${Path.MAUD_PATIENTS}/*`,
      );
    }
    return routes;
  }, [Object.keys(EAGER_MAUD_ROUTES).length, isRoutingRefactorPatientRoutesEnabled]);

  const lazyMaudRoutes = useMemo(
    () => parseLazyRoutes(LAZY_MAUD_ROUTES),
    [Object.keys(LAZY_MAUD_ROUTES).length],
  );

  const maudRedirectRouteOnLogin = getStartingRouteByProviderTypeName(
    currentUser.providerTypeDetails?.name ?? '',
  );

  return (
    <ComponentPortalContextProvider>
      <MaudProviders>
        <ChakraGrid
          columnGap="24px"
          height="100%"
          overflow="hidden"
          templateColumns="auto 1fr"
          templateRows="1fr"
          width="100%"
        >
          {isAiSummaryAppSyncEnabled && (
            <GlobalAppSyncSubscriptionManager />
          )}
          {phq9Enabled && (
            <>
              <SafetyRiskAlertSubscriptionManager />
              <SafetyRiskAlertContainer />
            </>
          )}
          <MaudNav />
          <Page>
            <Switch>
              <Route
                exact
                path={[Path.HOME, Path.LOGIN, Path.PORTAL]}
                render={() => (
                  <Redirect to={maudRedirectRouteOnLogin} />
                )}
              />
              {isRoutingRefactorPatientRoutesEnabled && (
                <Route
                  path={`${Path.MAUD_PATIENTS}/:patientId`}
                  component={PatientRoutes}
                />
              )}
              {eagerMaudRoutes.map(({ path, component }) => (
                <Route
                  component={component}
                  exact
                  key={`PageRoutes::${path}`}
                  path={path}
                />
              ))}
              {lazyMaudRoutes.map(({ path, component }) => {
                const LazyComponent = lazy(component);
                return (
                  <Route
                    exact
                    key={`PageRoutes::${path}`}
                    path={path}
                    render={() => (
                      <Suspense fallback={<Loading />}>
                        <LazyComponent />
                      </Suspense>
                    )}
                  />
                );
              })}
              <Route
                render={() => (
                  <Suspense fallback={<Loading />}>
                    <LazyPageNotFoundPage />
                  </Suspense>
                )}
              />
            </Switch>
            <AttachmentModal />
          </Page>
        </ChakraGrid>
      </MaudProviders>
    </ComponentPortalContextProvider>
  );
};

export default MaudApp;
